import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@wms/shared';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import type { SupplierItem } from '../product/types';
import { SkeletonRows } from '../skeleton';

/**
 * 供应商列表 + 右侧抽屉编辑（S9）。创建/编辑需 `supplier.manage`；无权限时
 * 字段只读且隐藏编辑按钮（S9 原文）。停用供应商不影响历史批次引用——
 * M4 阶段没有批次数据，这条语义留给 M5+ 验证，这里只保证停用本身可用。
 */
export function SupplierListPage(): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const canManage = session?.permissions.includes(PERMISSIONS.SUPPLIER_MANAGE) ?? false;
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['suppliers', query],
    queryFn: () =>
      apiFetch<{ items: SupplierItem[] }>(
        `/suppliers${query ? `?query=${encodeURIComponent(query)}` : ''}`,
      ),
  });
  const items = data?.items ?? [];
  const selected = items.find((s) => s.id === selectedId) ?? null;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['suppliers'] });

  return (
    <div>
      <div className="row row--between">
        <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('nav.suppliers')}</h1>
        {canManage && (
          <button className="btn btn--primary btn--sm" onClick={() => setCreating(true)}>
            {t('suppliers.create')}
          </button>
        )}
      </div>

      <input
        className="field"
        style={{ marginBottom: 'var(--space-4)' }}
        placeholder={t('app.search')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {isPending && <SkeletonRows />}
      {!isPending && items.length === 0 && (
        <div className="empty-state">
          <p>{t('empty.noResults')}</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="table-scroll"><table className="table">
          <thead>
            <tr>
              <th>{t('suppliers.colCode')}</th>
              <th>{t('suppliers.colName')}</th>
              <th>{t('suppliers.colContact')}</th>
              <th>{t('suppliers.colStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedId(s.id)}>
                <td>{s.code}</td>
                <td>{s.name}</td>
                <td>{s.contactPerson ?? '—'}</td>
                <td>
                  {s.status === 'ACTIVE' ? (
                    <span className="badge badge--success">{t('suppliers.statusActive')}</span>
                  ) : (
                    <span className="badge badge--muted">{t('suppliers.statusInactive')}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {selected && (
        <SupplierDrawer
          supplier={selected}
          canManage={canManage}
          onClose={() => setSelectedId(null)}
          onChanged={refresh}
        />
      )}
      {creating && canManage && (
        <SupplierCreateDrawer onClose={() => setCreating(false)} onCreated={refresh} />
      )}
    </div>
  );
}

function SupplierDrawer({
  supplier,
  canManage,
  onClose,
  onChanged,
}: {
  readonly supplier: SupplierItem;
  readonly canManage: boolean;
  readonly onClose: () => void;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [name, setName] = useState(supplier.name);
  const [contactPerson, setContactPerson] = useState(supplier.contactPerson ?? '');
  const [phone, setPhone] = useState(supplier.phone ?? '');
  const [whatsapp, setWhatsapp] = useState(supplier.whatsapp ?? '');
  const [email, setEmail] = useState(supplier.email ?? '');
  const [address, setAddress] = useState(supplier.address ?? '');
  const [notes, setNotes] = useState(supplier.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/suppliers/${supplier.id}`, {
        method: 'PATCH',
        body: {
          name: name.trim(),
          contactPerson: contactPerson.trim() || undefined,
          phone: phone.trim() || undefined,
          whatsapp: whatsapp.trim() || undefined,
          email: email.trim() || undefined,
          address: address.trim() || undefined,
          notes: notes.trim() || undefined,
          version: supplier.version,
        },
      }),
    onSuccess: () => onChanged(),
    onError: (err) =>
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err)),
  });

  const toggleStatus = useMutation({
    mutationFn: () =>
      apiFetch(`/suppliers/${supplier.id}`, {
        method: 'PATCH',
        body: {
          status: supplier.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
          version: supplier.version,
        },
      }),
    onSuccess: () => onChanged(),
  });

  const archive = useMutation({
    mutationFn: () => apiFetch(`/suppliers/${supplier.id}/archive`, { method: 'POST' }),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer__header">
          <h2 className="drawer__title">{supplier.name}</h2>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>
            {t('app.close')}
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <label className="field">
            <span>{t('suppliers.colName')}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!canManage}
              required
            />
          </label>
          <label className="field">
            <span>{t('suppliers.contactPerson')}</span>
            <input
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              disabled={!canManage}
            />
          </label>
          <label className="field">
            <span>{t('suppliers.phone')}</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} disabled={!canManage} />
          </label>
          <label className="field">
            <span>{t('suppliers.whatsapp')}</span>
            <input
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              disabled={!canManage}
            />
          </label>
          <label className="field">
            <span>{t('suppliers.email')}</span>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!canManage}
              type="email"
            />
          </label>
          <label className="field">
            <span>{t('suppliers.address')}</span>
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              disabled={!canManage}
            />
          </label>
          <label className="field">
            <span>{t('suppliers.notes')}</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!canManage}
              rows={3}
            />
          </label>

          {error && <p className="field-error">{error}</p>}

          {canManage && (
            <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
              <button type="submit" className="btn btn--primary btn--sm" disabled={save.isPending}>
                {t('app.save')}
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => toggleStatus.mutate()}
              >
                {supplier.status === 'ACTIVE' ? t('suppliers.deactivate') : t('suppliers.activate')}
              </button>
              <button
                type="button"
                className="btn btn--danger btn--sm"
                onClick={() => archive.mutate()}
              >
                {t('suppliers.archive')}
              </button>
            </div>
          )}
        </form>
      </aside>
    </div>
  );
}

function SupplierCreateDrawer({
  onClose,
  onCreated,
}: {
  readonly onClose: () => void;
  readonly onCreated: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      apiFetch('/suppliers', { method: 'POST', body: { code: code.trim(), name: name.trim() } }),
    onSuccess: () => {
      onCreated();
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err)),
  });

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer__header">
          <h2 className="drawer__title">{t('suppliers.create')}</h2>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>
            {t('app.close')}
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <label className="field">
            <span>{t('suppliers.colCode')}</span>
            <input value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
          </label>
          <label className="field">
            <span>{t('suppliers.colName')}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          {error && <p className="field-error">{error}</p>}
          <button type="submit" className="btn btn--primary btn--sm" disabled={create.isPending}>
            {t('app.save')}
          </button>
        </form>
      </aside>
    </div>
  );
}
